import React, { useMemo, useState } from 'react';
import { Box, Tooltip, Typography, alpha, useTheme } from '@material-ui/core';
import { darken, lighten, makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment';
import { AccessTime, Check, DoneAll, ErrorOutline } from '@material-ui/icons';
import { BubbleContainer } from './BubbleContainer';
import { INoteContainerProps, NoteContainer } from './NoteContainer';
import { NoteMessageActions } from './NoteMessageActions';
import { getIntensityColor } from '../../utils';
import {
    MultimediaPreviewContent,
    OutboundMultimediaPreviewModal,
} from './multimedia';
import { InlineAudioPlayer } from './audio';
import {
    getOutboundPreviewMediaType,
    toMultimediaPreviewFromMessage,
} from 'src/views/OmniChannel/utils/outboundMultimedia';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
    slideLeft: {
        animation: '$slideLeft 600ms',
    },
    slideRight: {
        animation: '$slideRight 600ms',
    },
    reactionChip: {
        position: 'absolute',
        bottom: -10,
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        lineHeight: 1,
        cursor: 'default',
        userSelect: 'none',
        animation: '$reactionPop 220ms ease-out',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.18)',
    },
    reactionLeft: {
        left: -6,
    },
    reactionRight: {
        right: -6,
    },

    '@keyframes slideLeft': {
        '0%': {
            transform: 'translateX(-300px) scale(0.96, 1.04)',
        },
        '60%': {
            transform: 'translateX(1px)',
        },
        '65%': {
            transform: 'translateX(0px)',
        },
        '70%': {
            transform: 'scale(1.01, 0.99)',
        },
        '100%': {
            transform: 'scale(1,1)',
        },
    },

    '@keyframes slideRight': {
        '0%': {
            transform: 'translateX(300px) scale(0.96, 1.04)',
        },
        '60%': {
            transform: 'translateX(-1px)',
        },
        '65%': {
            transform: 'translateX(0px)',
        },
        '70%': {
            transform: 'scale(1.01, 0.99)',
        },
        '100%': {
            transform: 'scale(1,1)',
        },
    },

    '@keyframes reactionPop': {
        '0%': {
            opacity: 0,
            transform: 'scale(0.55) translateY(4px)',
        },
        '70%': {
            opacity: 1,
            transform: 'scale(1.12) translateY(0)',
        },
        '100%': {
            opacity: 1,
            transform: 'scale(1) translateY(0)',
        },
    },
});

export const MessageItem = ({
    message,
    fullWidth = false,
    dateFormat = 'HH:mm a',
}: {
    message: any;
    fullWidth?: boolean;
    dateFormat?: string;
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme<{
        palette: Theme['palette'] & { background: { dark: string } };
    }>();
    const [openMediaPreview, setOpenMediaPreview] = useState(false);

    const {
        id,
        origin,
        isSeen,
        name,
        mediaUrl,
        mediaContentType,
        content,
        createdAt,
        reaction,
        reactions,
        type,
        status,
        sentBy,
    } = message;

    const mediaPreview = useMemo(() => {
        if (!content?.media || content.media?.length === 0) return null;

        const media = content.media[0];

        const response = toMultimediaPreviewFromMessage({
            mediaUrl: media?.url,
            mediaContentType: media?.contentType,
        });

        return response;
    }, [content]);

    const mediaType = mediaPreview
        ? getOutboundPreviewMediaType(
              mediaPreview.name,
              mediaPreview.contentType,
              mediaPreview.source
          )
        : null;

    const shouldOpenModalOnClick =
        mediaType === 'image' ||
        mediaType === 'pdf' ||
        mediaType === 'unsupported';

    const isAudioMessage = mediaType === 'audio';

    const isNote = type === 'note' || message?.kind === 'note';
    const intensityColor = getIntensityColor(theme.palette.type);

    const getMessagePrimaryColor = () => {
        if (isNote) return lighten(theme.palette.warning.light, intensityColor);

        return origin === 'customer'
            ? lighten(theme.palette.primary.main, intensityColor)
            : theme.palette.primary.main;
    };

    const primaryColor = getMessagePrimaryColor();
    const textColor = theme.palette.getContrastText(primaryColor);

    const isOriginFromCustomer = origin === 'customer';
    const reactionBackgroundColor = theme.palette.secondary.main;
    const reactionBorderColor = darken(theme.palette.secondary.main, 0.08);
    const reactionTextColor = theme.palette.getContrastText(
        reactionBackgroundColor
    );
    const reactionCreatedAt = Array.isArray(reactions)
        ? reactions.find((item) => item?.emoji === reaction)?.createdAt
        : undefined;
    const reactionTooltip = reactionCreatedAt
        ? t('Chat.ReactedAt', {
              date: moment(reactionCreatedAt).format('DD/MM/YYYY HH:mm'),
          })
        : t('Chat.Reaction');

    // BubbleContainer accepts a subset of the note props and ignores fullWidth.
    const MessageContainer: React.ComponentType<INoteContainerProps> = isNote
        ? NoteContainer
        : BubbleContainer;

    const statusIconColor = isOriginFromCustomer
        ? theme.palette.primary.main
        : theme.palette.getContrastText(primaryColor);
    const readStatusIconColor =
        theme.palette.info?.main || theme.palette.secondary.main;

    const statusConfig = (() => {
        if (status === 'queued') {
            return {
                title: t('Chat.MessageStatusSending'),
                icon: (
                    <AccessTime
                        fontSize="small"
                        style={{ fontSize: 16, color: statusIconColor }}
                    />
                ),
            };
        }

        if (status === 'failed') {
            return {
                title: t('Chat.MessageStatusFailed'),
                icon: (
                    <ErrorOutline
                        fontSize="small"
                        style={{
                            fontSize: 16,
                            color: theme.palette.error.main,
                        }}
                    />
                ),
            };
        }

        if (status === 'read' || isSeen) {
            return {
                title: t('Chat.MessageStatusRead'),
                icon: (
                    <DoneAll
                        fontSize="small"
                        style={{
                            fontSize: 16,
                            color: readStatusIconColor,
                        }}
                    />
                ),
            };
        }

        if (status === 'delivered') {
            return {
                title: t('Chat.MessageStatusDelivered'),
                icon: (
                    <DoneAll
                        fontSize="small"
                        style={{ fontSize: 16, color: statusIconColor }}
                    />
                ),
            };
        }

        return {
            title: t('Chat.MessageStatusSent'),
            icon: (
                <Check
                    fontSize="small"
                    style={{ fontSize: 16, color: statusIconColor }}
                />
            ),
        };
    })();

    const handleOpenMediaPreview = () => {
        if (!mediaPreview || !shouldOpenModalOnClick) return;
        setOpenMediaPreview(true);
    };

    return (
        <Box
            className={
                isOriginFromCustomer ? classes.slideLeft : classes.slideRight
            }
            display="flex"
            justifyContent={isOriginFromCustomer ? 'flex-start' : 'flex-end'}
            mb={1}
            ml={isOriginFromCustomer ? 2 : 0}
            mr={isOriginFromCustomer ? 0 : 2}
        >
            <MessageContainer
                position={isOriginFromCustomer ? 'left' : 'right'}
                bgColor={primaryColor}
                fullWidth={fullWidth}
            >
                <Box
                    style={{
                        color: theme.palette.getContrastText(primaryColor),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    <Typography
                        variant="h4"
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            fontWeight: 'bold',
                        }}
                    >
                        {isNote &&
                            `${t('Chat.NoteBy')} ` + (sentBy?.name ?? name)}
                    </Typography>

                    {mediaPreview && isAudioMessage && (
                        <Box style={{ minWidth: 220, maxWidth: 320 }}>
                            <InlineAudioPlayer
                                src={mediaPreview.source}
                                activeColor={theme.palette.getContrastText(
                                    primaryColor
                                )}
                                inactiveColor={alpha(
                                    theme.palette.getContrastText(primaryColor),
                                    0.45
                                )}
                            />
                        </Box>
                    )}

                    {mediaPreview && !isAudioMessage && (
                        <MultimediaPreviewContent
                            source={mediaPreview.source}
                            name={mediaPreview.name}
                            contentType={mediaPreview.contentType}
                            variant="inline"
                            onMediaClick={
                                shouldOpenModalOnClick
                                    ? handleOpenMediaPreview
                                    : undefined
                            }
                        />
                    )}

                    {isNote ? (
                        <NoteMessageActions
                            messageId={id}
                            messageText={content?.text}
                            textColor={textColor}
                        />
                    ) : (
                        content?.text && (
                            <Typography variant="body1">
                                {content?.text}
                            </Typography>
                        )
                    )}

                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        <Typography
                            variant="caption"
                            color={'inherit'}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {moment(createdAt).format(dateFormat)}
                        </Typography>
                        <Typography
                            variant="caption"
                            color={'inherit'}
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Tooltip title={statusConfig.title}>
                                {statusConfig.icon}
                            </Tooltip>
                        </Typography>
                    </Box>

                    {reaction && (
                        <Tooltip title={reactionTooltip} placement="top" arrow>
                            <Box
                                className={`
                                    ${classes.reactionChip}
                                    ${
                                        isOriginFromCustomer
                                            ? classes.reactionRight
                                            : classes.reactionLeft
                                    }
                                `}
                                style={{
                                    background: reactionBackgroundColor,
                                    border: `1px solid ${reactionBorderColor}`,
                                    color: reactionTextColor,
                                }}
                            >
                                {reaction}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </MessageContainer>

            <OutboundMultimediaPreviewModal
                open={openMediaPreview}
                setOpen={setOpenMediaPreview}
                preview={mediaPreview}
            />
        </Box>
    );
};
